import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import Nav from "../components/Nav";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { MaskedLines } from "../components/Reveal";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/portal";

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back.");
            navigate(from, { replace: true });
        } catch (e2) {
            const msg = formatApiErrorDetail(e2.response?.data?.detail) || e2.message;
            setErr(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground">
            <Nav />
            <div className="mx-auto max-w-[1600px] px-6 md:px-10 pt-32 pb-24 grid grid-cols-12 gap-6">
                <div className="col-span-12 md:col-span-6">
                    <div className="text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-6 flex items-center gap-3">
                        <span className="w-8 h-px bg-muted-foreground" />
                        Client Portal
                    </div>
                    <MaskedLines
                        as="h1"
                        className="font-editorial text-6xl md:text-8xl tracking-tighter leading-[0.9]"
                        lines={["Welcome", <><span className="italic text-accent">back.</span></>]}
                    />
                    <p className="mt-8 text-muted-foreground max-w-md text-base leading-relaxed">
                        Sign in to review dispute status, letter history, and your live credit-score
                        trajectory.
                    </p>
                    <div className="mt-16 font-mono-ui text-xs text-muted-foreground border-t hairline pt-6">
                        Demo access · client@example.com / Client@123
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    className="col-span-12 md:col-span-5 md:col-start-8 bg-background border hairline p-8 md:p-12"
                    data-testid="login-form"
                >
                    <div className="space-y-8">
                        <label className="block">
                            <span className="block text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-3">
                                Email
                            </span>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email"
                                className="w-full bg-transparent border-b hairline focus:border-foreground outline-none py-3 font-mono-ui text-sm"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-3">
                                Password
                            </span>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password"
                                className="w-full bg-transparent border-b hairline focus:border-foreground outline-none py-3 font-mono-ui text-sm"
                            />
                        </label>
                        {err && (
                            <div data-testid="login-error" className="text-sm text-destructive">
                                {err}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit"
                            className="group w-full inline-flex items-center justify-between gap-3 bg-foreground text-background px-6 py-4 text-[13px] tracking-[0.16em] uppercase hover:bg-accent disabled:opacity-60"
                        >
                            {loading ? "Signing in…" : "Sign in"}
                            <ArrowUpRight size={16} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </button>
                        <div className="text-xs font-mono-ui text-muted-foreground">
                            New here?{" "}
                            <Link to="/register" className="underline underline-offset-4 text-foreground">
                                Create an account
                            </Link>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
